import {
  AllUserMonarxDetails200RowsItem,
  UserHosting200RowsItem,
} from '@newfold/huapi-js/src/index.schemas';

import { HEALTH_STATUS_INFECTED } from '~/scenes/Site/scenes/Security/utils/constants';
import { ADVANCED_HOSTING_TYPES } from '~/utils/hosting/constants';
import { getHostingDisabled } from '~/utils/hosting/helpers';

import {
  HostingPlan,
  HowToCardContent,
  HowToCardContentParams,
  PublicImageItem,
} from './types';

const HOW_TO_CARD_KEY_BY_PLAN = {
  dedicated: 'dediSelfManage.hostgator',
  vps: 'vpsSelfManage.hostgator',
} as const;

// Calculate percentage utility
export const calculatePercentage = (
  used?: number | null, // in Kib
  total?: number | string | null, // in Kib
): number | null => {
  if (used == null || total == null || total === 0 || total === 'unlimited')
    return null;

  return Math.min(Math.round((used / Number(total)) * 100), 100);
};

// Get total disk quota bases on hosting type, hosting status and server status
export const getTotalDiskQuotaGib = (
  plan: UserHosting200RowsItem,
): string | number | null => {
  const { limit = {}, limit_remote = {}, account_info = {} } = plan || {};
  const { type, server_status } = account_info || {};

  const isAtomic = type === 'cluster_atomic';
  const isAdvanced = ADVANCED_HOSTING_TYPES.includes(type ?? '');

  const defaultDiskTotal = getHostingDisabled(account_info.addon_status ?? '')
    ? null
    : 'unlimited';

  if (isAtomic) {
    return Number(limit?.disk_limit_gib) ?? defaultDiskTotal;
  }

  if (isAdvanced && limit_remote?.quota_kib == null) {
    return server_status === 'active' && type !== 'vps'
      ? defaultDiskTotal
      : null;
  }

  return limit_remote?.quota_kib == null
    ? defaultDiskTotal
    : Number(limit_remote?.quota_kib || 0) / (1024 * 1024);
};

// Format disk usage (in GB)
export const getDiskTotalGib = (inputInGib: number): string => {
  const valueInGib = Number(inputInGib);
  const value = Number.isInteger(valueInGib)
    ? valueInGib
    : Number(valueInGib.toFixed(2));
  return `${value}GB`;
};

// Format disk usage (in KB, MB, or GB)
export function getDiskUsedGib(
  input: number,
  total: number | string | null,
): string {
  const valueInKiB = Number(input);
  if (!input || Number.isNaN(valueInKiB)) return '0';

  const valueInMB = valueInKiB / 1024;
  const valueInGB = (valueInMB / 1024).toFixed(2);

  return typeof total === 'number' && Number.isFinite(total)
    ? valueInGB
    : `${valueInGB}GB`;
}

export const getServerStatusandColor = (serverStatus: string | undefined) => {
  const status = serverStatus ?? '';

  const STATUS_CONFIG: Record<string, { color?: string; status: string }> = {
    pending_resize: { status: 'loading' },
    resize: { status: 'loading' },

    start: { color: 'success.main', status: 'starting' },
    pending_start: { color: 'success.main', status: 'starting' },

    active: { color: 'success.main', status: 'online' },

    failed_start: { color: 'error.main', status: 'offline' },
    decom: { color: 'error.main', status: 'offline' },
    deleted: { color: 'error.main', status: 'offline' },
    stopped: { color: 'error.main', status: 'offline' },

    reboot_soft: { color: 'error.main', status: 'rebooting' },
    pending_reboot_soft: { color: 'error.main', status: 'rebooting' },

    stop: { color: 'error.main', status: 'poweringOff' },
    pending_stop: { color: 'error.main', status: 'poweringOff' },
  };

  const REIMAGE_STATUSES = new Set([
    'pending_postprov',
    'pending_create',
    'create',
    'pending_build',
    'build',
    'postprov',
    'pending_rebuild',
    'rebuild',
  ]);

  if (REIMAGE_STATUSES.has(status)) {
    return { status: 'reimaging' };
  }

  return STATUS_CONFIG[status] ?? { status: 'loading' };
};

// Map hosting plan to view model
export const mapPlanToView = (plan?: UserHosting200RowsItem): HostingPlan => {
  const {
    used = {},
    limit = {},
    account_info = {},
    back_reference: backReference,
    billing = {},
    is_upgradeable: isUpgradeable,
    feature_set: featureSet,
    panel: isPanel,
    sites: numberOfSitesAvailable,
    has_panel_mail: hasPanelMail,
  } = plan || {};

  const {
    id,
    type,
    username,
    ns = [],
    ip = '',
    region,
    ssh_hostname,
    os_name,
    os_version,
    application,
    addon_status,
    server_type,
    meta,
    server_status,
    date_added,
    server_subtype,
    software,
    addon_date_added,
    image = {},
  } = account_info || {};

  const isAtomic = type === 'cluster_atomic';
  const isReseller =
    ['reseller', 'tr_reseller'].includes(server_subtype ?? '') ||
    server_type === 'shared_reseller_cpanel';
  const isAdvanced = ADVANCED_HOSTING_TYPES.includes(type ?? '');

  const diskUsedSource = used?.disk_usage;

  const totalDiskQuotaGib = getTotalDiskQuotaGib(plan ?? {});

  const diskUsed =
    typeof diskUsedSource === 'number' && Number.isFinite(diskUsedSource)
      ? getDiskUsedGib(diskUsedSource, totalDiskQuotaGib)
      : diskUsedSource;

  const diskUsedPercentage =
    typeof diskUsedSource === 'number' && Number.isFinite(totalDiskQuotaGib)
      ? calculatePercentage(
          diskUsedSource,
          Number(totalDiskQuotaGib) * 1024 * 1024, // convert Gib to Kib
        )
      : null;

  const diskTotal =
    typeof totalDiskQuotaGib === 'number' && Number.isFinite(totalDiskQuotaGib)
      ? getDiskTotalGib(totalDiskQuotaGib)
      : totalDiskQuotaGib;

  const {
    auto_renew_flag: autoRenewFlag,
    expiration_date: expirationDate,
    prod_name: productName,
    prod_inst_name: productInstName,
  } = billing || {};

  return {
    autoRenewFlag,
    expirationDate,
    title: productName,
    diskUsed,
    diskUsedPercentage,
    diskTotal,
    websitesUsed: used?.sites || 0,
    websitesTotal: limit?.sites ?? 'unlimited',
    domainsUsed: used?.domain || 0,
    phpWorkersUsed: used?.workers || 0,
    phpWorkersTotal: limit?.workers ?? 0,
    atomic: isAtomic,
    advanced: isAdvanced,
    status: addon_status,
    backReference: backReference || id,
    hostingPlanId: id,
    addonDateAdded: addon_date_added,
    isReseller,
    domainName: productInstName || meta?.main_domain,
    type,
    username,
    isUpgradeable,
    featureSet,
    isPanel,
    numberOfSitesAvailable,
    dateAdded: date_added,
    hasPanelMail: hasPanelMail,
    software,
    image,
    details: {
      nameServers: ns,
      sshKey: ip ? `${username}@${ip}` : username,
      ftpAccess: 'Enabled',
      ipAddress: ip,
      dataCenter: region?.desc || 'Unknown',
      hostname: ssh_hostname || '',
      operatingSystem: os_name || 'Unknown',
      osVersion: os_version || '',
      application: application || '',
      ssh_hostname: ssh_hostname || '',
      serverStatus: server_status || '',
    },
  };
};
export const findHostingPlanById = <T extends UserHosting200RowsItem>(
  plans: T[] | undefined,
  hostingId: string | undefined,
): T | undefined => {
  if (!plans || !hostingId) {
    return undefined;
  }
  return plans.find((plan) => {
    const { id, addon_back_ref } = plan?.account_info || {};
    return id === hostingId || addon_back_ref === hostingId;
  });
};

export const getAppLinks = (
  docsUrl: string = '',
  communityUrl?: string,
  forumsUrl?: string,
  getStartedUrl?: string,
) => {
  return [
    { url: docsUrl, urlText: 'documentation' },
    { url: communityUrl, urlText: 'community' },
    { url: forumsUrl, urlText: 'forum' },
    { url: getStartedUrl, urlText: 'getStarted' },
  ];
};

export const getPlanStatus = (plans: UserHosting200RowsItem[] = []): string => {
  return plans.some(
    (plan: UserHosting200RowsItem) =>
      plan.account_info?.addon_status === 'active',
  )
    ? 'active'
    : 'inactive';
};

export const isSitesAvailable = (
  advanced: boolean,
  numberOfSitesAvailable: number,
  isReseller: boolean | undefined,
) => {
  if (advanced) {
    return numberOfSitesAvailable > 0;
  }
  if (isReseller) {
    return false;
  }
  //For shared_cpanel always return true
  return true;
};

// Group user images by OS name, only include 'base' type images
export const groupImagesByType = (
  images: PublicImageItem[] = [],
): Record<string, PublicImageItem[]> => {
  return images.reduce<Record<string, PublicImageItem[]>>((acc, image) => {
    const { os_name, type } = image;
    if (type !== 'base') return acc; /// Skip non-'base' images
    const key = os_name || 'unknown';
    (acc[key] ||= []).push(image);
    return acc;
  }, {});
};

export const fetchCurrentImage = (images: PublicImageItem[] = []) => {
  return images.find((img) => img?.current === 1);
};

export const groupAllMonarxAddonsByHostingId = (
  monarxAddonList: AllUserMonarxDetails200RowsItem[],
) => {
  return monarxAddonList.reduce(
    (
      addonsByHostingId: Record<string, AllUserMonarxDetails200RowsItem[]>,
      addon: AllUserMonarxDetails200RowsItem,
    ) => {
      const { account_back_ref: hostingID } = addon;
      if (!hostingID) return addonsByHostingId;
      (addonsByHostingId[hostingID] ||= []).push(addon);
      return addonsByHostingId;
    },
    {},
  );
};

export const getHostingPlanMonarxInfo = (
  monarxAddonGroupedList: Record<string, AllUserMonarxDetails200RowsItem[]>,
  backReference: string | null,
  domainName?: string | null,
): AllUserMonarxDetails200RowsItem | undefined => {
  if (!backReference) return undefined;
  const hostingPlanMonarxList = monarxAddonGroupedList?.[backReference];
  if (!hostingPlanMonarxList) return undefined;

  const isDetected = (row: AllUserMonarxDetails200RowsItem) =>
    (row.health_status?.toLowerCase() ?? '') === HEALTH_STATUS_INFECTED;

  const domainLower = domainName?.trim().toLowerCase();
  if (domainLower) {
    const matchCardDomain = hostingPlanMonarxList.find(
      (row) =>
        isDetected(row) &&
        row.domain_name?.trim().toLowerCase() === domainLower,
    );
    if (matchCardDomain) return matchCardDomain;
  }

  return hostingPlanMonarxList.find(isDetected);
};

/**
 * Generates test ID for buttons in reimage modals
 * @param identifier - The identifier (app name or OS display name)
 * @param buttonType - The type of button ('cancel' or 'proceed')
 * @returns The formatted test ID
 */
export const generateVPSAppButtonTestId = (
  identifier: string,
  buttonType: string,
): string => {
  if (!identifier) {
    return `self-manage-${buttonType}-btn`;
  }
  return `self-manage-${identifier?.replace(/\s+/g, '-')}-${buttonType}-btn`;
};

export const getShortDescription = (text?: string) => {
  if (!text) return '';

  // Find the first ". " that is not inside a URL.
  // A URL starts with "://" and ends at the next whitespace — so if there is an
  // open "://" with no space between it and the current dot, skip that dot.
  const sentenceEnd = /\. /g;
  let match = sentenceEnd.exec(text);

  while (match !== null) {
    const { index: dotPosition } = match;
    const before = text.slice(0, dotPosition);
    const lastUrlStart = before.lastIndexOf('://');
    const dotIsInsideUrl =
      lastUrlStart !== -1 && !before.slice(lastUrlStart).includes(' ');

    if (!dotIsInsideUrl) return text.slice(0, dotPosition + 1);

    match = sentenceEnd.exec(text);
  }

  return text;
};

export const isSvgOrPng = (logo: string) =>
  typeof logo === 'string' && /\.(svg|png)$/i.test(logo);

export const isSelfManagedDedicated = (
  planType?: string | null,
  prodName?: string | null,
) => planType === 'vps' && prodName?.toLowerCase().includes('dedicated');

export const getSelfManagedPlanName = (isSelfManagedDedi: boolean) =>
  isSelfManagedDedi ? 'Dedicated Server' : 'VPS';

export const getHowToCardContent = ({
  isHostgator,
  isSelfManagedDedi,
  dediOverviewArticleUrl,
  vpsOverviewArticleUrl,
}: HowToCardContentParams): HowToCardContent => {
  if (!isHostgator) {
    return { title: '', description: '', articleUrl: '' };
  }
  const plan = isSelfManagedDedi ? 'dedicated' : 'vps';
  const key = HOW_TO_CARD_KEY_BY_PLAN[plan];
  const articleUrl =
    plan === 'dedicated'
      ? dediOverviewArticleUrl ?? ''
      : vpsOverviewArticleUrl ?? '';
  return {
    title: `${key}.title`,
    description: `${key}.desc`,
    articleUrl,
  };
};
